import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import {
  cognitoUserPoolsTokenProvider,
  FetchUserAttributesOutput,
  ResendSignUpCodeOutput,
} from 'aws-amplify/auth/cognito';
import {
  confirmSignUp,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  signIn,
  SignInInput,
  SignInOutput,
  signOut,
  updatePassword,
  AuthError,
} from '@aws-amplify/auth';
import { CookieStorage, fetchAuthSession } from '@aws-amplify/core';
import { catchError, forkJoin, from, map, Observable, of, take, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getUserName, Role, SimpleUser } from '../models/user';
import { UserAlreadyAuthenticatedError } from '../models/authentication-errors/user-already-authenticated-error';
import { UserNotAuthorizedError } from '../models/authentication-errors/user-not-authorized-error';
import { UserNotFoundError } from '../models/authentication-errors/user-not-found-error';
import { UnknownAuthenticationError } from '../models/authentication-errors/unknown-authentication-error';

@Injectable({
  providedIn: 'root',
})
export class OAuthService {
  constructor() {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: environment.cognito.userPoolId,
          userPoolClientId: environment.cognito.userPoolWebClientId,
        },
      },
    });
    cognitoUserPoolsTokenProvider.setKeyValueStorage(new CookieStorage());
  }

  isLoggedIn(): Observable<boolean> {
    return from(getCurrentUser()).pipe(
      map(() => {
        return true;
      }),
      catchError(() => {
        return of(false);
      }),
    );
  }

  getUserAttributes(): Observable<FetchUserAttributesOutput> {
    return from(fetchUserAttributes());
  }

  /**
   * Return simple user built from informations contained in the token
   * @returns SimpleUser
   */
  getSimpleUser(): Observable<SimpleUser> {
    return forkJoin([
      this.getUserApplicationRoles().pipe(take(1)),
      this.getUserAttributes().pipe(take(1)),
    ]).pipe(
      map(([appRoles, userAttributes]) => {
        const role =
          appRoles.includes('Admin') || appRoles.includes('ContentManager')
            ? Role.Admin
            : Role.User;
        const email = userAttributes.email || '';
        return {
          userId: userAttributes.sub || '',
          email,
          username: getUserName(userAttributes.given_name, userAttributes.family_name) || email,
          role,
          organisationId: 'unknown',
        };
      }),
    );
  }

  emailVerified(): Observable<boolean> {
    return from(fetchUserAttributes()).pipe(
      map((attributes) => attributes.email_verified == 'true'),
    );
  }

  getUserApplicationRoles(): Observable<string[]> {
    return from(fetchAuthSession()).pipe(
      map((session) => (session.tokens?.idToken?.payload['cognito:groups'] as string[]) || []),
    );
  }

  confirmSignUp(email: string, code: string): Observable<boolean> {
    return from(confirmSignUp({ username: email, confirmationCode: code })).pipe(
      map((confirm) => confirm.isSignUpComplete),
    );
  }

  /**
   * Authenticates a user by signing them using their credentials.
   *
   * @function
   * @param {SignInInput} params - An object containing the user's sign-in credentials.
   * @param {string} params.username - The username of the user attempting to sign in.
   * @param {string} params.password - The password associated with the specified username.
   * @returns {Observable<SignInOutput>} An observable that emits `SignInOutput`: When authentication is successful.
   *
   * @throws {UserAlreadyAuthenticatedError} When the user is already authenticated.
   * @throws {ApplicationAuthentificationError} When authentication fails due to incorrect credentials.
   * @throws {UnknownAuthenticationError} For any other unexpected errors.
   */
  signIn({ username, password }: SignInInput): Observable<SignInOutput> {
    return from(signIn({ username, password })).pipe(
      catchError((error) => {
        if (this.isAuthError(error)) {
          switch (error.name) {
            case 'UserAlreadyAuthenticatedException':
              return throwError(() => new UserAlreadyAuthenticatedError(error.message));
            case 'NotAuthorizedException':
              return throwError(() => new UserNotAuthorizedError(error.message));
            case 'UserNotFoundException':
              return throwError(() => new UserNotFoundError(error.message));
            default:
              return throwError(() => new UnknownAuthenticationError(error.message));
          }
        }
        return throwError(() => new UnknownAuthenticationError('An unknown error occurred.'));
      }),
    );
  }

  resendConfirmCode(email: string): Observable<ResendSignUpCodeOutput> {
    return from(resendSignUpCode({ username: email }));
  }

  changePassword(oldPassword: string, newPwd: string): Observable<void> {
    return from(updatePassword({ oldPassword: oldPassword, newPassword: newPwd }));
  }

  signOut(): Observable<void> {
    return from(signOut());
  }

  /**
   * Retrieve Access token from the auth session or guest service depending on user role
   * @param forceRefresh - Force token refresh from Cognito
   * @returns Access token value
   */
  getAccessToken(forceRefresh = false): Observable<string> {
    return from(fetchAuthSession({ forceRefresh })).pipe(
      map(
        (refreshedSession) => refreshedSession.tokens?.accessToken?.toString() ?? 'no token found',
      ),
      catchError((error) => {
        console.error('Error fetching Access token', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Refresh the access token by forcing a session refresh
   * @returns Refreshed access token value
   */
  refreshAccessToken(): Observable<string> {
    return this.getAccessToken(true);
  }

  /**
   * Type guard to ensure the result is a `SignInOutput`
   *
   * @param {unknow} result The authentication result
   * @returns  `true` if the result is a `SignInOutput`, otherwise false
   */
  isSignInOutput(result: unknown): result is SignInOutput {
    // Check if result is not null and contains next step
    return typeof result === 'object' && result !== null && 'nextStep' in result;
  }

  /**
   * Type guard to ensure the result is a `AuthError`
   *
   * @param {unknow} error The authentication error
   * @returns  `true` if the result is a `AuthError`, otherwise false
   */
  isAuthError(error: unknown): error is AuthError {
    // Check if error message is not null and contains name and message
    return typeof error === 'object' && error !== null && 'name' in error && 'message' in error;
  }

  /**
   * Type guard to check if the given step is a `DoneSignInStep`
   *
   * This ensure that the sign in process has been fully completed
   *
   * @param {AuthNextSignInStep<UserAttributeKey>} step The authentication step to check
   * @returns  `true` if the step is a `DoneSignInStep`, otherwise false
   */
  isDoneSignInStep(step: SignInOutput['nextStep']): boolean {
    return step.signInStep === 'DONE';
  }

  /**
   * Type guard to check if the given step is a `ConfirmSignUpStep`
   *
   * This ensure that the sign in process waiting for user confirmation
   *
   * @param {AuthNextSignInStep<UserAttributeKey>} step the authentication step to check
   * @returns `true` if the step is a `ConfirmSignUpStep`, otherwise false
   */
  isConfirmSignUpStep(step: SignInOutput['nextStep']): boolean {
    return step.signInStep === 'CONFIRM_SIGN_UP';
  }
}
